import { useState } from "react";
import {
  CalendarDaysIcon,
  EllipsisIcon,
  NotebookPenIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";

import { CompanyNoteSheet } from "./CompanyNoteSheet";
import { Column, ColumnHeader, ConfirmationDialog, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useDeleteCompanyNote } from "@/hooks/mutations/useDeleteCompanyNote";
import { useRemoveCompanyNote } from "@/hooks/sets/useRemoveCompanyNote";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { CompanyNote } from "@/types";

const DropdownActions = ({ companyNoteId }: { companyNoteId: number }) => {
  const {
    mutate: deleteCompanyNote,
    isPending: isDeletePending,
    isError: isDeleteError,
  } = useDeleteCompanyNote();
  const removeCompanyNote = useRemoveCompanyNote();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleConfirmDelete = () => {
    deleteCompanyNote(
      { companyNoteId },
      {
        onSuccess: () => {
          removeCompanyNote({ companyNoteId });
          setIsDeleteDialogOpen(false);
        },
      },
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost">
            <EllipsisIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-red-500 hover:!text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              setIsDeleteDialogOpen(true);
            }}
          >
            <Trash2Icon className="h-4 w-4" />
            Delete note
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmationDialog
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description="Are you sure you want to delete this note? This action cannot be undone."
        errorMessage="Failed to delete note. Please try again."
        isError={isDeleteError}
        isLoading={isDeletePending}
        onConfirm={handleConfirmDelete}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        preventContentClickPropagation
        title="Delete Note"
      />
    </>
  );
};

export const CompanyNotesTable = ({
  className,
  companyNotes,
}: {
  className?: string;
  companyNotes: CompanyNote[];
}) => {
  const {
    selectedResourceId: selectedCompanyNoteId,
    setSelectedResourceId: setSelectedCompanyNoteId,
  } = useSelectedResourceIdFromUrl({
    paramName: "n",
  });

  // Find the selected company note based on the ID from URL.
  const selectedCompanyNote = selectedCompanyNoteId
    ? (companyNotes.find((note) => note.id === selectedCompanyNoteId) ?? null)
    : null;

  const renderTableHeader = () => {
    return (
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[32%]">
                <ColumnHeader icon={NotebookPenIcon} label="Title" />
              </TableHead>
              <TableHead className="w-[20%]">
                <ColumnHeader icon={UsersIcon} label="Created By" />
              </TableHead>
              <TableHead className="w-[20%]">
                <ColumnHeader icon={CalendarDaysIcon} label="Creation Date" />
              </TableHead>
              <TableHead className="w-[20%]">
                <ColumnHeader icon={CalendarDaysIcon} label="Last Modified" />
              </TableHead>
              <TableHead className="w-[8%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
    );
  };

  const renderTableBody = () => {
    return (
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {companyNotes.map((companyNote: CompanyNote) => {
              return (
                <TableRow
                  key={companyNote.id}
                  onClick={() => {
                    setSelectedCompanyNoteId(companyNote.id);
                  }}
                >
                  <TableCell className="w-[32%]">
                    <Text>{companyNote.title}</Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {companyNote.user.firstName} {companyNote.user.lastName}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {dates.formatIsoDateForUser(companyNote.createdAt)}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {dates.formatIsoDateForUser(companyNote.updatedAt)}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[8%]">
                    <DropdownActions companyNoteId={companyNote.id} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderSheets = () => {
    return (
      <CompanyNoteSheet
        companyNote={selectedCompanyNote}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedCompanyNoteId(null);
          }
        }}
        open={!!selectedCompanyNoteId}
      />
    );
  };

  return (
    <Column className={cn("overflow-auto", className)}>
      {renderTableHeader()}
      {renderTableBody()}
      {renderSheets()}
    </Column>
  );
};
